import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, MapPin, Users, DollarSign, Ticket, 
  TrendingUp, ExternalLink, Lock
} from 'lucide-react';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import EventSubNav from '../../components/EventSubNav';
import { eventsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const EventDashboardPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [eventId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eventRes, dashboardRes] = await Promise.all([
        eventsApi.getById(eventId),
        eventsApi.getDashboard(eventId),
      ]);
      setEvent(eventRes.data);
      setDashboard(dashboardRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos del evento');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      if (newStatus === 'completed') {
        await eventsApi.markCompleted(eventId);
      } else if (event?.status === 'completed' && newStatus === 'published') {
        await eventsApi.republish(eventId);
      } else {
        await eventsApi.update(eventId, { status: newStatus });
      }
      const statusLabels = {
        published: 'Publicado',
        private: 'Privado',
        draft: 'Borrador',
        completed: 'Ya Realizado'
      };
      toast.success(`Estado cambiado a ${statusLabels[newStatus] || newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-[#1A1A1A] w-1/3 rounded" />
            <div className="grid grid-cols-4 gap-6">
              {[1,2,3,4].map(i => <div key={i} className="h-32 bg-[#1A1A1A] rounded" />)}
            </div>
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <div data-testid="event-dashboard-page">
          <EventSubNav eventTitle={event?.title} />

          {/* Event Info Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between mb-8 p-4 bg-[#121212] border border-[#27272A]"
          >
            <div className="flex items-center gap-6 text-[#A1A1AA]">
              <span className="flex items-center gap-2">
                <Calendar size={16} />
                {event?.date && formatInTimeZone(new Date(event.date), 'America/Argentina/Buenos_Aires', "d 'de' MMMM, yyyy", { locale: es })}
              </span>
              <span className="flex items-center gap-2">
                <MapPin size={16} />
                {event?.location}
              </span>
              {event?.service_fee > 0 && (
                <span className="text-sm">
                  Servicio: ${event.service_fee.toLocaleString()}
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              {/* Link to public event page */}
              <Link
                to={`/evento/${eventId}`}
                target="_blank"
                className="inline-flex items-center gap-2 px-4 py-2 border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-white transition-colors"
              >
                <ExternalLink size={16} />
                Ver Página Pública
              </Link>

              {/* Status selector */}
              <Select 
                value={event?.status} 
                onValueChange={handleStatusChange}
                disabled={event?.status === 'completed'}
              >
                <SelectTrigger className={`w-44 border-[#27272A] ${
                  event?.status === 'published' ? 'text-green-500' :
                  event?.status === 'private' ? 'text-yellow-500' :
                  event?.status === 'completed' ? 'text-purple-500' :
                  'text-[#A1A1AA]'
                }`}>
                  <SelectValue>
                    <span className="flex items-center gap-2">
                      {event?.status === 'private' && <Lock size={14} />}
                      {event?.status === 'published' ? 'Publicado' : 
                       event?.status === 'private' ? 'Privado' : 
                       event?.status === 'completed' ? 'Ya Realizado' :
                       'Borrador'}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border border-[#27272A]">
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="published">Publicado</SelectItem>
                  <SelectItem value="private">Privado</SelectItem>
                  <SelectItem value="completed">Ya Realizado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          >
            <div className="card-dashboard">
              <div className="flex items-center justify-between mb-4">
                <Ticket size={24} className="text-blue-500" />
                <TrendingUp size={16} className="text-green-500" />
              </div>
              <p className="text-3xl font-black">{dashboard?.tickets_sold || 0}</p>
              <p className="text-[#A1A1AA] text-sm">de {dashboard?.total_tickets || 0} tickets</p>
              <div className="mt-3 h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 transition-all"
                  style={{ width: `${((dashboard?.tickets_sold || 0) / (dashboard?.total_tickets || 1)) * 100}%` }}
                />
              </div>
            </div>

            <div className="card-dashboard">
              <div className="flex items-center justify-between mb-4">
                <DollarSign size={24} className="text-green-500" />
                <TrendingUp size={16} className="text-green-500" />
              </div>
              <p className="text-3xl font-black">${(dashboard?.total_revenue || 0).toLocaleString()}</p>
              <p className="text-[#A1A1AA] text-sm">Ingresos totales</p>
            </div>

            <div className="card-dashboard">
              <div className="flex items-center justify-between mb-4">
                <Users size={24} className="text-purple-500" />
              </div>
              <p className="text-3xl font-black">{dashboard?.orders_count || 0}</p>
              <p className="text-[#A1A1AA] text-sm">Órdenes completadas</p>
            </div>

            <div className="card-dashboard">
              <div className="flex items-center justify-between mb-4">
                <DollarSign size={24} className="text-orange-500" />
              </div>
              <p className="text-3xl font-black">${(dashboard?.avg_order_value || 0).toLocaleString()}</p>
              <p className="text-[#A1A1AA] text-sm">Promedio por orden</p>
            </div>
          </motion.div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sales Chart */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="card-dashboard"
            >
              <h3 className="text-lg font-bold uppercase tracking-tight mb-4">Ventas (30 días)</h3>
              <div className="h-48 flex items-end justify-between gap-1">
                {dashboard?.sales_by_day?.slice(-14).map((day, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center">
                    <div 
                      className="w-full bg-blue-500/20 hover:bg-blue-500/40 transition-colors rounded-t"
                      style={{ 
                        height: `${Math.max(4, (day.revenue / Math.max(...dashboard.sales_by_day.map(d => d.revenue || 1))) * 100)}%`
                      }}
                      title={`${day.date}: $${day.revenue}`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-2 text-xs text-[#52525B]">
                <span>Hace 14 días</span>
                <span>Hoy</span>
              </div>
            </motion.div>

            {/* Top Discount Codes */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="card-dashboard"
            >
              <h3 className="text-lg font-bold uppercase tracking-tight mb-4">Top Códigos de Descuento</h3>
              {dashboard?.top_discount_codes?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.top_discount_codes.map((code, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-[#27272A]">
                      <span className="font-mono font-bold">{code.code}</span>
                      <span className="text-[#A1A1AA]">{code.uses} usos · ${code.revenue.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#52525B] text-center py-8">No hay códigos usados aún</p>
              )}
            </motion.div>
          </div>
        </div>
        </AdminLayout>
      </SidebarProvider>
  );
};

export default EventDashboardPage;
